<script setup lang="ts">
//===============================================
//                   Imports
//===============================================
import * as L from "leaflet"
import "leaflet/dist/leaflet.css"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import {
    App,
    createApp,
    h,
    nextTick,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
} from "vue"
import { loadPrimeVue } from "@server/primevue"
import i18nPlugin from "@core/plugins/i18n.plugin"
import MapBuildingPopup from "@modules/energy-manager/components/dashboard/building/MapBuildingPopup.vue"

import defaultIconUrl from "@icons/building/marker.svg?url"
import { formatNumber } from "@core/helpers"
import { computedAndFormatEvolution } from "@modules/energy-manager/helpers"
import { VARIABLES } from "@modules/energy-manager/constants"

const defaultIcon = new L.Icon({
    iconUrl: defaultIconUrl,
    iconRetinaUrl: defaultIconUrl,
    iconSize: [40, 40], // Ajuste la taille selon ton besoin
    iconAnchor: [20, 40], // Ajuste l'ancrage de l'icône
    popupAnchor: [0, -20], // Position du popup
    shadowUrl: "", // Pas d'ombre pour un SVG
})

const emConsoCompareStore = useEmConsoCompareStore()

const mapContainer = ref(null)

const popupData = reactive<{
    data: {
        name: string
        address: string
        sources: number
        buildingId: string
        onClick: (buildingId: string) => void
        data: string | null
        variable: string
        evolution: number | null
        evolutionFormated: string | null
        rankingOrder: "asc" | "desc"
    }
}>({
    data: {
        name: "",
        address: "",
        sources: 0,
        buildingId: "",
        onClick: (buildingId: string) => {
            emConsoCompareStore.clickLink(buildingId, "building")
        },

        /**
         * valeur sur la période formatée
         */
        data: null,
        /**
         * variable de la donnée
         */
        variable: "",
        /**
         * évolution sur la période formatée
         */
        evolution: null,
        /**
         * évolution sur la période formatée
         */
        evolutionFormated: "",
        /**
         * Ordre de classement
         */
        rankingOrder: "desc",
    },
})
/**
 * Carte Leaflet
 */
const map = ref<L.Map | null>()
/**
 * Conteneur du popup
 */
const popupContainer = ref()
/**
 * Cluster de marqueurs (créé après chargement du plugin)
 */
const markersCluster = ref<L.MarkerClusterGroup | null>(null)
/**
 * Liste des marqueurs
 */
const markers = ref<L.Marker[]>([])

/**
 * Mapbox Static Tiles sont en 512×512 ; Leaflet attend 256×256 par défaut.
 * Sans `tileSize` / `zoomOffset`, la carte et les marqueurs se décalent au zoom (souvent visible sur Windows).
 */
const MAPBOX_TILE_LAYER_OPTIONS: L.TileLayerOptions = {
    tileSize: 512,
    zoomOffset: -1,
    attribution:
        '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
}

let mapResizeObserver: ResizeObserver | null = null

onMounted(async () => {
    // Initialise la carte
    initMap()

    if (!map.value) return

    await nextTick()
    map.value.invalidateSize()
    requestAnimationFrame(() => map.value?.invalidateSize())

    if (mapContainer.value) {
        mapResizeObserver = new ResizeObserver(() => {
            map.value?.invalidateSize({ animate: false })
        })
        mapResizeObserver.observe(mapContainer.value)
    }

    // Expose Leaflet sur window pour garantir que le plugin s'attache à la bonne instance
    const win = window as Window & { L?: typeof L }
    if (!win.L) {
        win.L = L
    }

    // Écoute l'événement 'load' de la carte pour garantir qu'elle est prête
    map.value.on("load", async () => {
        // Import dynamique du plugin APRÈS que Leaflet soit complètement chargé
        await import("leaflet.markercluster")

        // Utilise window.L pour garantir qu'on a l'instance augmentée par le plugin
        const LeafletGlobal = win.L ?? L

        // Crée le cluster maintenant que le plugin est chargé
        if (LeafletGlobal.markerClusterGroup) {
            markersCluster.value = LeafletGlobal.markerClusterGroup({
                maxClusterRadius: 40,
                disableClusteringAtZoom: 16,
            })

            // Charge le popup dans une application Vue
            loadPopup()

            // Charge les marqueurs
            loadMarkers()
        } else {
            console.error(
                "[MapBuilding] markerClusterGroup non disponible après import.",
                "L.markerClusterGroup:",
                L.markerClusterGroup,
                "window.L.markerClusterGroup:",
                LeafletGlobal.markerClusterGroup
            )
        }
    })

    // Force le déclenchement de l'événement 'load' si la carte est déjà prête
    // (cas où les tiles sont déjà en cache)
    map.value.whenReady(() => {
        if (!markersCluster.value) {
            map.value?.fire("load")
        }
    })
})

onBeforeUnmount(() => {
    mapResizeObserver?.disconnect()
    mapResizeObserver = null
    map.value?.remove()
    map.value = null
    markersCluster.value = null
    markers.value = []
})

/**
 * Fonction pour charger les marqueurs sur la carte
 * 1 - Supprime les anciens marqueurs
 * 2 - Ajoute les nouveaux marqueurs
 */
function loadMarkers() {
    if (!map.value || !markersCluster.value) return

    // supprime les anciens marqueurs
    markersCluster.value.clearLayers()
    markers.value.forEach((marker) => marker.remove())

    // Ajoute les nouveaux marqueurs
    addMarkers()
}

/**
 * Initialise la carte
 */
function initMap() {
    if (!mapContainer.value) return
    map.value = L.map(mapContainer.value).setView([48.8566, 2.3522], 10)
    L.tileLayer(
        `https://api.mapbox.com/styles/v1/mapbox/streets-v12/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoicnlhbnBlbnRhbG9nIiwiYSI6ImNrZThpNWd3ZTF3cDEzN243aWRqYzFqbnIifQ.FyaPeyRDdCTblU6bbNYmDQ`,
        MAPBOX_TILE_LAYER_OPTIONS
    ).addTo(map.value)
}

function loadI18n(app: App) {
    app.use(i18nPlugin)
}
/**
 * Charge le popup dans une application Vue
 */
function loadPopup() {
    // Créez un conteneur pour le popup
    popupContainer.value = document.createElement("div")
    // Montez le composant Vue dynamiquement dans ce conteneur
    const popupApp = createApp({
        render: () =>
            h(MapBuildingPopup, {
                data: popupData.data,
            }),
    })

    // Chargement des composants PrimeVue pour le popup
    loadPrimeVue(popupApp)

    loadI18n(popupApp)

    popupApp.mount(popupContainer.value)
}

/**
 * Ajoute les marqueurs sur la carte
 */
function addMarkers() {
    if (!map.value || !markersCluster.value) return

    // Liste des positions des marqueurs pour centrer la carte
    const markerPositions: L.LatLngExpression[] = []

    const splitVariable = (emConsoCompareStore.compareVariable ?? "")?.split(
        "."
    )?.[0]

    const variableConfig = VARIABLES[splitVariable]

    const maxDigit = parseInt(variableConfig.format?.match(/\d+/)?.[0] ?? "1")
    const unit = variableConfig?.unit ?? ""

    // Pour chaque bâtiment
    emConsoCompareStore.getComparedBuildingsList.forEach((building) => {
        if (building.location?.coordinates.length === 2) {
            const marker = L.marker(
                [
                    building.location.coordinates[1],
                    building.location.coordinates[0],
                ],
                { icon: defaultIcon }
            )

            const tooltipTitle =
                (building.name
                    ? building.name
                    : building.address?.inline_name) ?? ""

            if (tooltipTitle) {
                marker.bindTooltip(tooltipTitle, {
                    className: "marker-tooltip",
                    direction: "top",
                    offset: [0, -30],
                    opacity: 1,
                })
            }

            marker.bindPopup(popupContainer.value, {
                className: "custom-popup",
                interactive: true,
            })
            // ajoute le marker à la liste des marqueurs
            markers.value.push(marker)

            // Ajoute le marker au cluster de marqueurs
            // markersCluster.value est garanti non-null par le guard au début de addMarkers
            markersCluster.value!.addLayer(marker)
            // Ajoute la position du marker à la liste des positions
            markerPositions.push([
                building.location.coordinates[1],
                building.location.coordinates[0],
            ])

            // Gestion du clic sur chaque marker
            marker.on("click", () => {
                // Met à jour les données du popup Vue

                // Donnée de la seconde période (période actuelle)
                const firstData =
                    emConsoCompareStore.compareData?.[1]?.[
                        building._id_building
                    ]

                // Donnée de la première période (période comparée)
                const secondData =
                    emConsoCompareStore.compareData?.[0]?.[
                        building._id_building
                    ]

                const [evolution, evolutionFormated] =
                    computedAndFormatEvolution(firstData, secondData, {
                        maxDigit,
                        unit,
                    })

                const newPopupData = {
                    name: building.name,
                    address: building.address?.inline_name as string,
                    sources: building.sources.length,
                    buildingId: building._id_building,
                    data: formatNumber(
                        emConsoCompareStore.compareData[
                            emConsoCompareStore.compareData.length > 1 ? 1 : 0
                        ][building._id_building],
                        {
                            unit: variableConfig?.unit ?? "",
                            maximumFractionDigits: maxDigit,
                        }
                    ),
                    variable: splitVariable,
                    evolution: evolution as number,
                    evolutionFormated: evolutionFormated as string,
                    rankingOrder: (variableConfig?.ranking_order ?? "desc") as
                        | "asc"
                        | "desc",
                }

                popupData.data = { ...popupData.data, ...newPopupData }

                setTimeout(() => {
                    marker.openPopup()
                })
            })
        }
    })
    // Ajout les clusters de marqueurs à la carte
    map.value.addLayer(markersCluster.value as unknown as L.Layer)

    // Centrer la carte sur tous les marqueurs
    if (markerPositions.length > 0) {
        const bounds = L.latLngBounds(markerPositions) // Crée une zone qui englobe tous les marqueurs
        map.value.fitBounds(bounds) // Centre la carte sur cette zone
    }

    requestAnimationFrame(() => map.value?.invalidateSize({ animate: false }))
}

/**
 * Quand la liste des bâtiments comparés change, recharge les marqueurs
 * Quand le graphique de comparaison change, recharge les marqueurs
 */
watch(
    [
        () => emConsoCompareStore.getComparedBuildingsList,
        () => emConsoCompareStore.compareGraphData,
    ],
    () => {
        loadMarkers()
    }
)
</script>

<template>
    <div class="map-building" ref="mapContainer"></div>
</template>

<style scoped>
.map-building {
    height: 33.375rem;
    width: 100%;
}
</style>

<style>
.marker-tooltip {
    font-weight: 500;
}
/* .leaflet-tooltip:before,
.leaflet-tooltip:after {
    content: none !important;
}

.leaflet-fade-anim .leaflet-popup {
    transition: none !important;
    opacity: 1 !important;
} */

/* .leaflet-fade-anim .leaflet-popup {
    transition: none !important;
    opacity: 1 !important;
} */
</style>
